import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import { FaCheckCircle, FaHome, FaReceipt, FaTruck, FaExclamationTriangle } from 'react-icons/fa';
import { getOrderDetailsWithFallback } from '../services/orderApi';
import '@fortawesome/fontawesome-free/css/all.min.css';

export default function OrderConfirmation() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Récupérer les données depuis location.state OU localStorage OU paramètres d'URL
    const [orderDetails, setOrderDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    useEffect(() => {
        const loadOrderDetails = async () => {
            try {
                setLoading(true);
                setError(null);
                console.log('🔍 Recherche des détails de commande...');
                
                // 1. Essayer de récupérer depuis location.state
                if (location.state?.orderDetails) {
                    console.log('✅ Détails trouvés dans location.state');
                    setOrderDetails(location.state.orderDetails);
                    setLoading(false);
                    return;
                }
                
                // 2. Essayer de récupérer depuis les paramètres d'URL
                const urlParams = new URLSearchParams(window.location.search);
                const success = urlParams.get('success');
                const orderId = urlParams.get('order_id');
                
                console.log('🔍 Paramètres URL:', { success, orderId });

                // Stripe peut rediriger avec success=false après un paiement refusé/annulé :
                // ne jamais afficher une confirmation positive dans ce cas.
                if (success === 'false') {
                    console.log('❌ Paiement non confirmé (success=false)');
                    setOrderDetails({
                        orderId: orderId || null,
                        paymentStatus: 'failed',
                        amount: 0,
                        items: [],
                    });
                    setLoading(false);
                    return;
                }

                if (success === 'true' && orderId) {
                    console.log('✅ Tentative de récupération depuis l\'API avec orderId:', orderId);
                    try {
                        const orderDetails = await getOrderDetailsWithFallback(orderId);
                        setOrderDetails(orderDetails);
                        setLoading(false);
                        return;
                    } catch (apiError) {
                        console.warn('⚠️ Erreur API, utilisation du fallback:', apiError);
                    }
                }
                
                // 3. Essayer de récupérer depuis localStorage
                const lastOrder = localStorage.getItem('lastOrder');
                if (lastOrder) {
                    try {
                        const parsedOrder = JSON.parse(lastOrder);
                        console.log('✅ Détails trouvés dans localStorage:', parsedOrder);
                        setOrderDetails(parsedOrder);
                        setLoading(false);
                        return;
                    } catch (error) {
                        console.error('❌ Erreur parsing localStorage:', error);
                    }
                }
                
                // 4. Si aucune donnée n'est trouvée, créer des détails par défaut
                console.log('⚠️ Aucune donnée de commande trouvée, création de détails par défaut');
                const defaultOrderDetails = {
                    orderId: Math.floor(Math.random() * 1000000),
                    paymentStatus: 'unknown',
                    amount: 0,
                    items: [],
                    deliveryAddress: 'Adresse de livraison',
                    estimatedDelivery: '30-45 minutes'
                };
                setOrderDetails(defaultOrderDetails);
                setLoading(false);
                
            } catch (error) {
                console.error('❌ Erreur lors du chargement des détails:', error);
                setError(error.message);
                setLoading(false);
            }
        };
        
        loadOrderDetails();
    }, [location.state, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <NavBar />
                <div className="max-w-lg mx-auto px-4 py-16 animate-pulse space-y-4">
                    {/* Success icon placeholder */}
                    <div className="w-16 h-16 rounded-full bg-gray-200 mx-auto" />
                    {/* Title */}
                    <div className="h-7 bg-gray-200 rounded w-2/3 mx-auto" />
                    <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" />
                    {/* Order card */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-3 mt-4">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="flex justify-between items-center">
                                <div className="h-4 bg-gray-200 rounded w-1/3" />
                                <div className="h-4 bg-gray-200 rounded w-1/4" />
                            </div>
                        ))}
                    </div>
                    {/* Buttons */}
                    <div className="flex gap-3 mt-4">
                        <div className="flex-1 h-11 bg-gray-200 rounded-xl" />
                        <div className="flex-1 h-11 bg-gray-200 rounded-xl" />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-6">
                    <FaExclamationTriangle className="text-red-500 text-6xl mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('orderConfirmation.loadErrorTitle')}</h1>
                    <p className="text-gray-900 font-normal mb-6">{error}</p>
                    <div className="space-y-3">
                        <button
                            onClick={() => window.location.reload()}
                            className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
                        >
                            {t('orderConfirmation.retry')}
                        </button>
                        <Link
                            to="/"
                            className="block w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                            {t('orderConfirmation.backToHome')}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (!orderDetails) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-6">
                    <FaExclamationTriangle className="text-yellow-500 text-6xl mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('orderConfirmation.notFoundTitle')}</h1>
                    <p className="text-gray-900 font-normal mb-6">
                        {t('orderConfirmation.notFoundSubtitle')}
                    </p>
                    <div className="space-y-3">
                        <Link
                            to="/"
                            className="block w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
                        >
                            {t('orderConfirmation.backToHome')}
                        </Link>
                        <button
                            onClick={() => window.location.reload()}
                            className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                            {t('orderConfirmation.retry')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // paymentStatus peut valoir : 'succeeded' (payé), 'cash_on_delivery'/'pickup' (à régler plus
    // tard, commande néanmoins bien confirmée), 'pending' (paiement en ligne en cours de
    // confirmation côté serveur, ex. webhook Stripe pas encore reçu), ou 'failed' (paiement
    // explicitement refusé/annulé). Seul 'failed' doit afficher un échec au client.
    const isPaymentFailed = orderDetails.paymentStatus === 'failed';
    const isCashPending = orderDetails.paymentStatus === 'cash_on_delivery' || orderDetails.paymentStatus === 'pickup';
    const isPaymentPending = orderDetails.paymentStatus === 'pending';

    return (
        <>
            <NavBar />
            <div className="w-full pl-6 max-w-4xl mx-auto px-4">
                {/* Breadcrumb Navigation */}
                <nav className="flex items-center space-x-2 text-sm text-gray-900 font-normal mb-6 pt-5">
                    <Link to="/" className="flex items-center hover:text-green-600 transition-colors">
                        <FaHome className="w-4 h-4 mr-1" />
                        {t('orderConfirmation.breadcrumbHome')}
                    </Link>
                    <span className="text-black font-medium">{t('orderConfirmation.breadcrumbConfirmation')}</span>
                </nav>

                <div className="text-center py-12">
                    {/* Icône de succès / échec */}
                    <div className="mb-8">
                        {isPaymentFailed ? (
                            <FaExclamationTriangle className="text-red-500 text-6xl mx-auto" />
                        ) : (
                            <FaCheckCircle className="text-green-500 text-6xl mx-auto" />
                        )}
                    </div>

                    {/* Titre */}
                    <h1 className="text-3xl font-bold text-black mb-4">
                        {isPaymentFailed ? t('orderConfirmation.paymentNotConfirmedTitle') : t('orderConfirmation.orderConfirmedTitle')}
                    </h1>

                    {/* Numéro de commande */}
                    <div className="bg-white rounded-lg p-6 mb-8 max-w-md mx-auto">
                        <h2 className="text-xl font-semibold text-black mb-4">{t('orderConfirmation.orderDetailsTitle')}</h2>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-900 font-normal">{t('orderConfirmation.orderNumber')}</span>
                                <span className="font-semibold">#{orderDetails.orderId}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-900 font-normal">{t('orderConfirmation.status')}</span>
                                <span className={`font-semibold ${isPaymentFailed ? 'text-red-600' : 'text-green-600'}`}>
                                    {isPaymentFailed ? t('orderConfirmation.statusNotConfirmed') : t('orderConfirmation.statusConfirmed')}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-900 font-normal">{t('orderConfirmation.payment')}</span>
                                <span className={`font-semibold ${
                                    isPaymentFailed ? 'text-red-600'
                                    : (isCashPending || isPaymentPending) ? 'text-amber-600'
                                    : 'text-green-600'
                                }`}>
                                    {isPaymentFailed ? t('orderConfirmation.paymentFailed')
                                        : isCashPending ? t('orderConfirmation.paymentDueOnDelivery')
                                        : isPaymentPending ? t('orderConfirmation.paymentConfirming')
                                        : t('orderConfirmation.paymentPaid')}
                                </span>
                            </div>
                            {orderDetails.amount > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-gray-900 font-normal">{t('orderConfirmation.totalAmount')}</span>
                                    <span className="font-semibold">{orderDetails.amount.toFixed(2)} €</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Message de confirmation / échec */}
                    {isPaymentFailed ? (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8 max-w-2xl mx-auto">
                            <h3 className="text-lg font-semibold text-red-900 mb-3">
                                <FaExclamationTriangle className="inline mr-2" />
                                {t('orderConfirmation.paymentDeclinedTitle')}
                            </h3>
                            <p className="text-red-700">
                                {t('orderConfirmation.paymentDeclinedMessage')}
                            </p>
                        </div>
                    ) : (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8 max-w-2xl mx-auto">
                            <h3 className="text-lg font-semibold text-green-900 mb-3">
                                <FaTruck className="inline mr-2" />
                                {t('orderConfirmation.deliveryInProgressTitle')}
                            </h3>
                            <p className="text-green-700 mb-4">
                                {t('orderConfirmation.deliveryInProgressMessage')}
                            </p>
                            <div className="text-sm text-green-600">
                                <p>• {t('orderConfirmation.estimatedPrepTime')}</p>
                                <p>• {t('orderConfirmation.homeDelivery')}</p>
                                <p>
                                    •{' '}
                                    {isCashPending
                                        ? t('orderConfirmation.cashOnDeliveryNote')
                                        : isPaymentPending
                                        ? t('orderConfirmation.paymentConfirming')
                                        : t('orderConfirmation.paymentProcessedSecurely')}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/"
                            className="bg-grass-green text-white px-8 py-3 rounded-lg font-semibold hover:bg-grass-green-dark transition-colors flex items-center justify-center"
                        >
                            <FaHome className="mr-2" />
                            {t('orderConfirmation.backToHome')}
                        </Link>

                        <button
                            onClick={() => window.print()}
                            className="bg-white border border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center"
                        >
                            <FaReceipt className="mr-2" />
                            {t('orderConfirmation.printReceipt')}
                        </button>
                    </div>

                    {/* Informations supplémentaires */}
                    <div className="mt-12 text-gray-900">
                        <p className="mb-2">
                            {t('orderConfirmation.emailConfirmationSent')}
                        </p>
                        <p className="text-sm">
                            {t('orderConfirmation.contactUs')}
                        </p>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
} 